import React, { useEffect } from 'react';
import { HeartPulse } from 'lucide-react';
import { Card, CardHeader, CardFooter, AddButton, CollapsibleContent, EditModeToggle, EditModeCheckbox, EditModeActionBar } from '../shared';
import { InlineEditField } from '../shared/InlineEditField';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useFinancialData } from '../../contexts/FinancialDataContext';
import { useInlineEdit } from '../../hooks/useInlineEdit';
import { useCollapsedWidgets } from '../../hooks/useCollapsedWidgets';
import { useEditMode } from '../../hooks/useEditMode';
import { formatCurrency } from '../../utils/formatters';
import { HSA } from '../../types';

interface HSAWidgetProps {
  onAdd: () => void;
  variant?: 'card' | 'page';
}

export const HSAWidget: React.FC<HSAWidgetProps> = ({ onAdd, variant = 'card' }) => {
  const { darkMode, privacyMode } = useTheme();
  const { sheetId } = useAuth();
  const { hsaAccounts, updateHSA, totalHSA, handleDeleteHSA, handleBatchDelete } = useFinancialData();
  const { editValue, setEditValue, isEditing, startEdit, cancelEdit, completeEdit } = useInlineEdit();
  const { isCollapsed, toggleCollapse } = useCollapsedWidgets();
  const collapsed = isCollapsed('hsa');

  const handleDeleteAsync = async (index: number) => {
    if (!sheetId) return;
    await handleDeleteHSA(index, sheetId);
  };

  const handleBatchDeleteAsync = async (indices: number[]) => {
    if (!sheetId) return;
    await handleBatchDelete('HSA', indices, sheetId);
  };

  const editMode = useEditMode(handleDeleteAsync, hsaAccounts.length, handleBatchDeleteAsync);
  const { exitEditMode } = editMode;

  useEffect(() => {
    if (collapsed) exitEditMode();
  }, [collapsed, exitEditMode]);

  const handleSave = async (index: number) => {
    const { value, isValid } = completeEdit();
    if (isValid && sheetId) {
      await updateHSA(index, value, sheetId);
    }
  };

  const isPage = variant === 'page';

  return (
    <Card variant={variant}>
      <CardHeader
        title="HSA Accounts"
        icon={<HeartPulse size={20} />}
        action={
          <div className="flex items-center gap-2">
            <EditModeToggle
              isEditMode={editMode.isEditMode}
              onToggle={editMode.toggleEditMode}
              itemCount={hsaAccounts.length}
            />
            <AddButton onClick={onAdd} />
          </div>
        }
        collapsed={isPage ? false : collapsed}
        onToggleCollapse={isPage ? undefined : () => toggleCollapse('hsa')}
        variant={variant}
      />

      <CollapsibleContent collapsed={isPage ? false : collapsed} variant={variant}>
        {editMode.isEditMode && (
          <EditModeActionBar
            selectedCount={editMode.selectedIndices.size}
            totalCount={hsaAccounts.length}
            allSelected={editMode.selectedIndices.size === hsaAccounts.length}
            onSelectAll={() => editMode.selectAll(hsaAccounts.length)}
            onDeselectAll={editMode.deselectAll}
            onDeleteSelected={editMode.deleteSelected}
            isDeleting={editMode.isDeleting}
          />
        )}

        <div className="overflow-x-auto">
          {hsaAccounts.length === 0 ? (
            <div className={`text-center py-6 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              <p className="text-sm">No HSA accounts yet</p>
              <p className="text-xs mt-1">Add an HSA to track your health savings</p>
            </div>
          ) : (
            <table className="w-full text-sm [&_th]:px-2 [&_td]:px-2 md:[&_th]:px-1 md:[&_td]:px-1">
              <thead>
                <tr className={`${darkMode ? 'text-slate-400' : 'text-slate-600'} border-b ${darkMode ? 'border-white/10' : 'border-slate-200'}`}>
                  {editMode.isEditMode && <th className="w-8 pb-2" />}
                  <th className="text-left pb-2">Account</th>
                  <th className="text-right pb-2 w-[30%]">Balance</th>
                </tr>
              </thead>
              <tbody>
                {hsaAccounts.map((account: HSA, idx: number) => (
                  <tr
                    key={idx}
                    className={`${darkMode ? 'text-slate-300' : 'text-slate-800'} border-b ${darkMode ? 'border-white/10' : 'border-slate-100'}`}
                  >
                    {editMode.isEditMode && (
                      <td className="py-2 pr-2">
                        <EditModeCheckbox
                          checked={editMode.selectedIndices.has(idx)}
                          onToggle={() => editMode.toggleIndex(idx)}
                        />
                      </td>
                    )}
                    <td className="py-2 font-semibold">{account.name}</td>
                    <td className="text-right">
                      <div className="inline-block">
                        <InlineEditField
                          value={account.balance}
                          isEditing={isEditing('hsa', idx, 'balance')}
                          editValue={editValue}
                          onEditValueChange={setEditValue}
                          onStartEdit={() => startEdit('hsa', idx, 'balance', account.balance)}
                          onSave={() => handleSave(idx)}
                          onCancel={cancelEdit}
                          editable={account.editable}
                          variant="positive"
                          privacyMode={privacyMode}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </CollapsibleContent>

      <CardFooter>
        <div className="flex justify-between items-center font-semibold text-sm px-2 md:px-1">
          <span className={`${darkMode ? 'text-white' : 'text-slate-800'} flex-1`}>
            Total HSA
          </span>
          <span className={`${darkMode ? 'text-green-400' : 'text-green-600'} text-right w-24 md:w-28 ml-auto`}>
            {formatCurrency(totalHSA, privacyMode)}
          </span>
        </div>
      </CardFooter>
    </Card>
  );
};
